import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PlanCard from './PlanCard';
import CurrentSubscriptionCard from './CurrentSubscriptionCard';
import BillingPreferenceModal from './BillingPreferenceModal';
import '../../styles/SubscriptionTab.css';
import {useSubscription} from "../../context/SubscriptionContext";
import useSubscriptionApi from "../../hooks/useSubscriptionApi";
import {SUBSCRIPTION_STATUS} from "../../utils/constants";

/**
 * SubscriptionTab Component
 *
 * Subscription management component designed to work within a tab layout.
 * Unlike SubscriptionPlans, this doesn't have its own page wrapper/header.
 * Used in TrainerAccount tabs.
 */
const SubscriptionTab = () => {
    const { t } = useTranslation(['subscription', 'common']);
    const navigate = useNavigate();

    // Context
    const {
        subscriptionData,
        refreshSubscriptionStatus,
        getSubscriptionStatusText,
        formatDate,
        isInGracePeriod,
        canStartTrial
    } = useSubscription();

    // API hook
    const {
        loading: apiLoading,
        error: apiError,
        clearError,
        fetchPlans,
        upgradePlan,
        activateFree,
        openCustomerPortal,
        startTrial
    } = useSubscriptionApi();

    // Local state
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingPlan, setProcessingPlan] = useState(null);
    const [isStartingTrial, setIsStartingTrial] = useState(false);

    // Billing preference modal state
    const [showBillingModal, setShowBillingModal] = useState(false);
    const [selectedPlanForPurchase, setSelectedPlanForPurchase] = useState(null);

    // Sync API errors to local error state
    useEffect(() => {
        if (apiError) {
            setError(apiError);
        }
    }, [apiError]);

    // Fetch plans on mount
    useEffect(() => {
        loadPlans();
    }, []);

    /**
     * Load subscription plans
     */
    const loadPlans = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const fetchedPlans = await fetchPlans();
            setPlans(fetchedPlans);
        } catch (err) {
            // Error already handled by hook
        } finally {
            setLoading(false);
        }
    }, [fetchPlans]);

    /**
     * Handle plan selection (purchase or change)
     * Uses unified upgradePlan endpoint that handles both checkout and plan change scenarios
     */
    const handleSelectPlan = useCallback(async (plan) => {
        try {
            setProcessingPlan(plan.id);
            setError(null);

            const isActivePlan = subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE;
            const isTrialPlan = subscriptionData?.status === SUBSCRIPTION_STATUS.TRIAL;
            const isFree = plan.isFree || plan.price === 0;
            const hasStripeSubscription = subscriptionData?.hasStripeSubscription;

            // Free plan - use activateFree (permanent, no trial consumption)
            if (isFree) {
                // Only activate if not already on a subscription
                if (!isActivePlan && !isTrialPlan) {
                    await activateFree();
                    await refreshSubscriptionStatus();
                    alert(t('plansPage.freePlanActivated'));
                }
                setProcessingPlan(null);
                return;
            }

            // Paid plan purchase (no existing Stripe subscription) - show billing preference modal
            // This applies to users on FREE plan or with no subscription who want to BUY (not trial)
            if (!hasStripeSubscription && !isTrialPlan) {
                setSelectedPlanForPurchase(plan);
                setShowBillingModal(true);
                setProcessingPlan(null);
                return;
            }

            // Paid plan - confirm if already has active subscription (plan change)
            if (isActivePlan || isTrialPlan) {
                const confirmChange = window.confirm(
                    `${t('tab.confirmChange', { plan: plan.name })}${isActivePlan ? ` ${t('tab.confirmChangeProrated')}` : ''}`
                );

                if (!confirmChange) {
                    setProcessingPlan(null);
                    return;
                }
            }

            // Use unified upgradePlan endpoint
            // Backend automatically decides: checkout for FREE users, upgrade, or scheduled downgrade
            const result = await upgradePlan(plan.priceId);

            // If redirecting to checkout, the page will navigate away
            if (result.redirecting) {
                return;
            }

            // Handle different result types
            if (result.action === 'PLAN_CHANGED') {
                // Immediate upgrade
                alert(t('plansPage.planChanged'));
                await refreshSubscriptionStatus();
            } else if (result.action === 'DOWNGRADE_SCHEDULED') {
                // Downgrade scheduled for end of billing period
                const changeDate = result.scheduledChangeDate
                    ? new Date(result.scheduledChangeDate).toLocaleDateString(t('subscription:_locale', { defaultValue: 'pl-PL' }))
                    : t('plansPage.downgradeScheduledFallback');
                alert(t('plansPage.downgradeScheduled', { date: changeDate }));
                await refreshSubscriptionStatus();
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || t('plansPage.requestFailed'));
        } finally {
            setProcessingPlan(null);
        }
    }, [subscriptionData, upgradePlan, activateFree, refreshSubscriptionStatus]);

    /**
     * Handle billing preference confirmation from modal
     * Called after user selects invoice/personal billing and confirms data
     */
    const handleBillingConfirm = useCallback(async (billingData) => {
        if (!selectedPlanForPurchase) return;

        try {
            setShowBillingModal(false);
            setProcessingPlan(selectedPlanForPurchase.id);
            setError(null);

            // Store billing preference for potential use in checkout
            // The backend can use this information if needed
            sessionStorage.setItem('billingPreference', JSON.stringify(billingData));

            // Proceed with purchase
            const result = await upgradePlan(selectedPlanForPurchase.priceId);

            // If redirecting to checkout, the page will navigate away
            if (result.redirecting) {
                return;
            }

            // Handle different result types (should typically redirect to checkout for new purchases)
            if (result.action === 'PLAN_CHANGED') {
                alert(t('plansPage.planChanged'));
                await refreshSubscriptionStatus();
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || t('plansPage.requestFailed'));
        } finally {
            setProcessingPlan(null);
            setSelectedPlanForPurchase(null);
        }
    }, [selectedPlanForPurchase, upgradePlan, refreshSubscriptionStatus]);

    /**
     * Handle billing modal close
     */
    const handleBillingModalClose = useCallback(() => {
        setShowBillingModal(false);
        setSelectedPlanForPurchase(null);
        setProcessingPlan(null);
    }, []);

    /**
     * Handle starting trial (generic, no specific plan)
     */
    const handleStartTrial = useCallback(async () => {
        try {
            setIsStartingTrial(true);
            setError(null);

            await startTrial();
            await refreshSubscriptionStatus();
            alert(t('tab.trialActivated'));
        } catch (err) {
            setError(err.message || t('tab.trialFailed'));
        } finally {
            setIsStartingTrial(false);
        }
    }, [startTrial, refreshSubscriptionStatus]);

    /**
     * Handle starting trial for a specific paid plan
     */
    const handleStartTrialForPlan = useCallback(async (plan) => {
        try {
            setProcessingPlan(plan.id);
            setError(null);

            await startTrial(plan.priceId);
            await refreshSubscriptionStatus();
            alert(t('tab.trialActivated'));
        } catch (err) {
            setError(err.message || t('tab.trialFailed'));
        } finally {
            setProcessingPlan(null);
        }
    }, [startTrial, refreshSubscriptionStatus]);

    /**
     * Handle opening customer portal
     */
    const handleOpenPortal = useCallback(async () => {
        try {
            setLoading(true);
            await openCustomerPortal();
        } catch (err) {
            setError(t('plansPage.portalFailed'));
        } finally {
            setLoading(false);
        }
    }, [openCustomerPortal]);

    /**
     * Check if plan is the current active plan
     */
    const isCurrentPlan = (plan) => {
        // For FREE plan, check if user has no priceId and is ACTIVE (not TRIAL)
        // Users on FREE plan have status ACTIVE with no priceId and no Stripe subscription
        if (plan.isFree || plan.price === 0) {
            return (
                !subscriptionData?.priceId &&
                subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE &&
                !subscriptionData?.hasStripeSubscription
            );
        }
        // For paid plans, check priceId match
        return (
            subscriptionData?.priceId === plan.priceId &&
            (subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE ||
             subscriptionData?.status === SUBSCRIPTION_STATUS.TRIAL)
        );
    };

    /**
     * Clear error message
     */
    const handleClearError = () => {
        setError(null);
        clearError();
    };

    const hasActiveSubscription = subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE ||
                                   subscriptionData?.status === SUBSCRIPTION_STATUS.TRIAL;
    const userCanStartTrial = canStartTrial();
    const showTrialOption = userCanStartTrial && !hasActiveSubscription;

    // Loading state
    if (loading && plans.length === 0) {
        return (
            <div className="subscription-tab">
                <div className="subscription-tab__loading">
                    <div className="subscription-tab__spinner"></div>
                    <p>{t('tab.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="subscription-tab">
            {/* Section Header */}
            <div className="subscription-tab__header">
                <h2>{t('tab.title')}</h2>
                <p>{t('tab.subtitle')}</p>
            </div>

            {/* Error banner */}
            {error && (
                <div className="subscription-tab__error">
                    <p>{error}</p>
                    <button onClick={handleClearError}>×</button>
                </div>
            )}

            {/* Current subscription status */}
            <CurrentSubscriptionCard
                subscriptionData={subscriptionData}
                isInGracePeriod={isInGracePeriod()}
                onOpenPortal={handleOpenPortal}
                getStatusText={getSubscriptionStatusText}
                formatDate={formatDate}
            />

            {/* Trial offer for new users */}
            {showTrialOption && (
                <div className="subscription-tab__trial-offer">
                    <div className="trial-offer__content">
                        <div className="trial-offer__icon">🎁</div>
                        <div className="trial-offer__text">
                            <h3>{t('tab.trialTitle')}</h3>
                            <p>
                                {t('tab.trialDescription')}
                            </p>
                        </div>
                        <button
                            className="trial-offer__button"
                            onClick={handleStartTrial}
                            disabled={isStartingTrial}
                        >
                            {isStartingTrial ? t('tab.activating') : t('tab.startFree')}
                        </button>
                    </div>
                </div>
            )}

            {/* Plans section */}
            <div className="subscription-tab__plans-section">
                <h3>{t('tab.availablePlans')}</h3>

                {plans.length === 0 ? (
                    <div className="subscription-tab__empty">
                        <p>{t('plansPage.noPlans')}</p>
                        <button onClick={loadPlans}>{t('common:refresh')}</button>
                    </div>
                ) : (
                    <div className="subscription-tab__plans-grid">
                        {plans.map((plan) => (
                            <PlanCard
                                key={plan.id}
                                plan={plan}
                                isCurrentPlan={isCurrentPlan(plan)}
                                isProcessing={processingPlan === plan.id}
                                hasActiveSubscription={hasActiveSubscription}
                                canStartTrial={userCanStartTrial}
                                onSelect={handleSelectPlan}
                                onStartTrial={handleStartTrialForPlan}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Help section */}
            <div className="subscription-tab__help">
                <p>
                    {t('tab.helpQuestion')}{' '}
                    <a href="mailto:help@petboard.pl">{t('tab.contactUs')}</a>
                </p>
            </div>

            {/* Billing Preference Modal */}
            <BillingPreferenceModal
                isOpen={showBillingModal}
                onClose={handleBillingModalClose}
                onConfirm={handleBillingConfirm}
                planName={selectedPlanForPurchase?.name || ''}
                planPrice={selectedPlanForPurchase?.priceFormatted || selectedPlanForPurchase?.price + ' zł/mies.'}
            />
        </div>
    );
};

export default SubscriptionTab;